// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import { fileURLToPath } from "url"
import { errorMessage, exists, isDirectory, isRecord, readJsonSafe } from "./util"
import * as Filesystem from "../../util/filesystem"

export type LegacySessionIndexEntry = {
  legacySessionId: string
  title?: string
  created?: number
  workspaceDirectory?: string
}

export type ProjectDiscoveryIssue = {
  kind: "sessions-json-invalid" | "workspace-missing"
  sourcePath: string
  workspaceDirectory?: string
  message?: string
}

export async function discoverProjectDirectories(home: string): Promise<{
  projects: string[]
  issues: ProjectDiscoveryIssue[]
}> {
  const sessionsPath = path.join(home, ".infcode", "sessions", "sessions.json")
  if (!(await exists(sessionsPath))) {
    return { projects: [], issues: [] }
  }

  let value: unknown
  try {
    value = JSON.parse(await fs.readFile(sessionsPath, "utf8"))
  } catch (err) {
    return {
      projects: [],
      issues: [
        {
          kind: "sessions-json-invalid",
          sourcePath: sessionsPath,
          message: errorMessage(err),
        },
      ],
    }
  }

  const seen = new Set<string>()
  const projects: string[] = []
  const issues: ProjectDiscoveryIssue[] = []
  for (const directory of extractWorkspaceDirectories(value)) {
    const resolved = resolveWorkspaceDirectory(directory)
    if (seen.has(resolved)) continue
    seen.add(resolved)
    if (await isDirectory(resolved)) {
      projects.push(resolved)
      continue
    }
    issues.push({
      kind: "workspace-missing",
      sourcePath: sessionsPath,
      workspaceDirectory: resolved,
    })
  }
  return { projects, issues }
}

export async function readSessionIndex(home: string): Promise<{
  entries: Map<string, LegacySessionIndexEntry>
  issues: ProjectDiscoveryIssue[]
}> {
  const sessionsPath = path.join(home, ".infcode", "sessions", "sessions.json")
  if (!(await exists(sessionsPath))) return { entries: new Map(), issues: [] }

  const parsed = await readJsonSafe(sessionsPath)
  if (!parsed.ok) {
    return {
      entries: new Map(),
      issues: [
        {
          kind: "sessions-json-invalid",
          sourcePath: sessionsPath,
          message: parsed.message,
        },
      ],
    }
  }

  const entries = new Map<string, LegacySessionIndexEntry>()
  for (const item of extractSessionIndexItems(parsed.value)) {
    const legacySessionId = text(item.sessionId) ?? text(item.sessionID) ?? text(item.id)
    if (!legacySessionId) continue
    const workspace = legacyWorkspaceDirectory(item)
    entries.set(legacySessionId, {
      legacySessionId,
      ...(text(item.title) ?? text(item.name) ? { title: text(item.title) ?? text(item.name) } : {}),
      ...(timestamp(item.dateCreated ?? item.createdAt ?? item.timeCreated ?? record(item.time)?.created) !== undefined
        ? { created: timestamp(item.dateCreated ?? item.createdAt ?? item.timeCreated ?? record(item.time)?.created) }
        : {}),
      ...(workspace ? { workspaceDirectory: workspace } : {}),
    })
  }
  return { entries, issues: [] }
}

export function resolveWorkspaceDirectory(directory: string): string {
  const trimmed = directory.trim()
  // Use Filesystem.resolve to normalize paths AND resolve symlinks,
  // matching the canonicalization used by KiloSession.filters when querying.
  // This ensures migrated sessions are discoverable even when workspace paths
  // contain symlinks (e.g., /tmp → /private/tmp on macOS).
  if (trimmed.startsWith("file://")) return Filesystem.resolve(fileURLToPath(trimmed))
  if (path.isAbsolute(trimmed) || /^[A-Za-z]:[\\/]/.test(trimmed)) return Filesystem.resolve(trimmed)
  return Filesystem.resolve(trimmed)
}

function extractSessionIndexItems(value: unknown): Record<string, unknown>[] {
  const source = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.sessions)
      ? value.sessions
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : []
  return source.filter(isRecord)
}

export function legacyWorkspaceDirectory(item: Record<string, unknown>): string | undefined {
  const raw = text(item.workspaceDirectory) ?? text(item.directory) ?? text(item.cwd)
  return raw ? resolveWorkspaceDirectory(raw) : undefined
}

function extractWorkspaceDirectories(value: unknown): string[] {
  const source = extractSessionIndexItems(value)
  const result: string[] = []
  for (const item of source) {
    const directory = legacyWorkspaceDirectory(item)
    if (directory) result.push(directory)
  }
  return result
}

function record(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function timestamp(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value !== "string" || !value.trim()) return undefined
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : undefined
}
